import { Either } from "../../core/func/monad";
import { Progress } from "../../core/progress";
import { voidFunc } from "../../core/voidFunc";
import { HttpError, HttpResult } from "../../infra/HttpResult";
import { NotReady } from "./load_status";

export class LoadingResult<E, T> implements Either<NotReady<E>, T> {
  private constructor(
    private readonly _raw:
      | { type: "not-ready"; data: NotReady<E> }
      | { type: "ready"; data: T }
  ) {}

  fold<LeftOut, RightOut>(
    onLeft: (arg: NotReady<E>) => LeftOut,
    onRight: (arg: T) => RightOut
  ) {
    if (this._raw.type === "not-ready") {
      return onLeft(this._raw.data);
    }
    return onRight(this._raw.data);
  }

  match<LeftOut, RightOut>(matcher: {
    notReady: (arg: NotReady<E>) => LeftOut;
    ready: (arg: T) => RightOut;
  }): LeftOut | RightOut {
    if (this._raw.type === "not-ready") {
      return matcher.notReady(this._raw.data);
    }
    return matcher.ready(this._raw.data);
  }

  map<U>(f: (input: T) => U): Either<NotReady<E>, U> {
    if (this._raw.type === "not-ready") {
      return new LoadingResult<E, U>({
        type: "not-ready",
        data: this._raw.data,
      });
    }
    return new LoadingResult({
      type: "ready",
      data: f(this._raw.data),
    });
  }

  isInProgress(): boolean {
    return (
      this._raw.type === "not-ready" &&
      this._raw.data.type === Progress.InProgress
    );
  }

  isReady(): boolean {
    return this._raw.type === "ready";
  }

  isErr(): boolean {
    return (
      this._raw.type === "not-ready" && this._raw.data.type === Progress.Error
    );
  }

  getErr(): E | undefined {
    return this._raw.type === "not-ready" &&
      this._raw.data.type === Progress.Error
      ? this._raw.data.err
      : undefined;
  }

  getReadyData(): T | undefined {
    return this._raw.type === "ready" ? this._raw.data : undefined;
  }

  isNotStarted(): boolean {
    return (
      this._raw.type === "not-ready" &&
      this._raw.data.type === Progress.NotStarted
    );
  }

  static from<T>(data: T): LoadingResult<any, T> {
    return new LoadingResult({
      type: "ready",
      data,
    });
  }

  static fromHttpResult<T>(data: HttpResult<T>): LoadingResult<HttpError, T> {
    return data.match({
      err: (err) => LoadingResult.err(err),
      ok: (ok) => LoadingResult.from(ok),
    });
  }

  static notStarted<E>(): LoadingResult<E, any> {
    return new LoadingResult({
      type: "not-ready",
      data: { type: Progress.NotStarted },
    });
  }

  static inProgress<E>(): LoadingResult<E, any> {
    return new LoadingResult({
      type: "not-ready",
      data: { type: Progress.InProgress },
    });
  }

  static err<E>(err: E): LoadingResult<E, any> {
    return new LoadingResult({
      type: "not-ready",
      data: { type: Progress.Error, err },
    });
  }

  static anyNotReady<E>(
    ...results: LoadingResult<E, any>[]
  ): LoadingResult<E, any> | undefined {
    for (const result of results) {
      const notReady = result.match({
        notReady: () => true,
        ready: voidFunc,
      });
      if (notReady) {
        return result;
      }
    }
  }

  static anyErr<E>(
    ...results: LoadingResult<E, any>[]
  ): LoadingResult<E, any> | undefined {
    for (const result of results) {
      const err = result.match({
        notReady: (data) => data.type === Progress.Error,
        ready: voidFunc,
      });
      if (err) {
        return result;
      }
    }
  }

  static matchBothReady<E, T>(
    left: LoadingResult<E, T>,
    right: LoadingResult<E, T>,
    onMatched: (left: T, right: T) => void
  ): void {
    left.match({
      notReady: voidFunc,
      ready: (leftData) => {
        right.match({
          notReady: voidFunc,
          ready: (rightData) => {
            onMatched(leftData, rightData);
          },
        });
      },
    });
  }
}
